// Advanced Follow-up Sequences System
import portfolioAnalytics from './analytics';

class FollowUpSequences {
  constructor() {
    this.sequences = this.initializeSequences();
    this.activeSequences = new Map(); // Track active sequences for users
  }

  initializeSequences() {
    return {
      // Digital Marketing Sequences
      'digital-marketing': {
        name: 'Digital Marketing Lead Nurture',
        triggers: ['form_submit', 'pricing_view', 'cta_click'],
        sequence: [
          {
            delay: 0, // Immediate
            type: 'welcome_email',
            subject: '🚀 Let\'s Turn Your Brand Into a Profit Machine',
            content: `Hey [NAME],

Thank you for your interest in growing your brand! I'm excited to help you build marketing campaigns that actually drive results (not just pretty numbers).

I noticed you're interested in digital marketing services. Here's what makes my approach different:

✅ I focus on ROI, not vanity metrics
✅ Every campaign is built to convert followers into customers  
✅ You get transparent reporting and measurable results

NEXT STEPS:
📞 I'll reach out within 24 hours to discuss your specific goals
📊 Meanwhile, check out these recent results: [CASE_STUDY_LINK]
🎯 Have urgent questions? Reply to this email or call +91 96020 03790

Ready to scale your brand?
Harish Kumar

P.S. - I only work with 3-4 brands at a time to ensure exceptional results. Let's see if we're a good fit!`,
            actionItems: ['send_case_studies', 'schedule_call_reminder']
          },
          {
            delay: 24 * 60 * 60 * 1000, // 24 hours
            type: 'value_email',
            subject: '📈 The 3-Step System That Grew [CLIENT] to 27K+ Monthly Views',
            content: `[NAME],

Since you're interested in digital marketing, I wanted to share the exact 3-step system I used to grow Valyoued.co.in from 0 to 27K+ monthly views:

STEP 1: Strategic Positioning 🎯
- Found their unique angle in the psychology/wellness space
- Created content pillars that resonate with their target audience
- Established thought leadership positioning

STEP 2: Content That Converts 📱
- Developed viral-worthy content formats
- Built engagement sequences that nurture followers
- Created conversion-focused call-to-actions

STEP 3: Automation & Scale 🚀  
- Set up automated lead funnels
- Implemented email sequences that sell while you sleep
- Built systems that work 24/7

RESULT: 5X ROI and a thriving online community

Want to see how this system would work for your brand? 

Let's chat: [CALENDAR_LINK]

Best,
Harish`,
            actionItems: ['share_case_study', 'calendar_link']
          },
          {
            delay: 3 * 24 * 60 * 60 * 1000, // 3 days
            type: 'social_proof_email',
            subject: '🔥 Why FintelDefence Chose Me Over 47 Other Agencies',
            content: `[NAME],

Quick story that might interest you...

Last month, FintelDefence (fintech cybersecurity) interviewed 47 marketing agencies.

They chose me. Here's why:

❌ Other agencies promised "brand awareness"
✅ I promised qualified B2B leads and measurable ROI

❌ Other agencies showed generic case studies  
✅ I showed specific results in their industry

❌ Other agencies had teams of junior marketers
✅ I personally handle strategy and execution

THE RESULT:
📈 300% increase in LinkedIn engagement
💼 Generated 50+ qualified B2B leads  
🎯 8X ROI on advertising spend
📊 Positioned as thought leader in their niche

The difference? I don't just run ads - I build complete marketing systems that grow businesses.

Ready to see what this looks like for your brand?

Book a call: [CALENDAR_LINK]
Or reply with "RESULTS" to see more case studies

Talk soon,
Harish

P.S. - Still thinking it over? That's smart. Check out my live case study at valyoued.co.in to see the system in action.`,
            actionItems: ['book_call', 'send_case_studies']
          },
          {
            delay: 7 * 24 * 60 * 60 * 1000, // 1 week
            type: 'urgency_email',
            subject: '[NAME] - Limited Availability for Q1 2025',
            content: `[NAME],

I wanted to reach out personally because I noticed your interest in growing your brand through strategic marketing.

Here's the thing - I intentionally limit myself to working with only 3-4 brands at a time. Why? Because when you're building campaigns that deliver 5X+ ROI, you need to give each client your full attention.

CURRENT AVAILABILITY:
✅ 1 spot available for January 2025
⚠️  2 spots for February (likely to fill soon)
❌ March is already fully booked

If you're serious about:
🚀 Building a brand that actually drives sales
📈 Getting measurable results (not just vanity metrics)  
💰 Seeing real ROI from your marketing investment

Then let's talk this week.

Book your strategy call: [CALENDAR_LINK]

Or if you have questions, just reply to this email.

Best regards,
Harish Kumar

P.S. - No pressure, but the brands I work with see results like:
• 27K+ monthly reach (Valyoued)
• 300% LinkedIn growth (FintelDefence)  
• 5X average ROI across all clients

Your results could be next.`,
            actionItems: ['book_strategy_call', 'create_urgency']
          }
        ]
      },

      // Web Development Sequences
      'web-development': {
        name: 'Web Development Lead Nurture',
        triggers: ['form_submit', 'pricing_view', 'portfolio_view'],
        sequence: [
          {
            delay: 0,
            type: 'welcome_email',
            subject: '💻 Ready to Build a Website That Actually Converts?',
            content: `Hi [NAME],

Thanks for your interest in professional web development! 

I noticed you're looking for development services. Here's what makes working with me different:

✅ I build websites that convert visitors into customers (not just look pretty)
✅ Every line of code is written with performance and SEO in mind
✅ You get a partner who understands both technology AND business growth

WHAT'S NEXT:
🔧 I'll analyze your current situation and send recommendations within 24 hours
⚡ Check out my recent work: valyoued.co.in (95+ PageSpeed score!)
📞 Have urgent questions? Call +91 96020 03790

Let's build something amazing together!

Harish Kumar
Full-Stack Developer

P.S. - I only take on 2-3 development projects at a time to ensure exceptional quality and personal attention.`,
            actionItems: ['site_audit', 'portfolio_showcase']
          },
          {
            delay: 24 * 60 * 60 * 1000, // 24 hours
            type: 'technical_value_email',
            subject: '⚡ Why Your Website Speed Could Be Killing Your Sales',
            content: `[NAME],

Did you know that a 1-second delay in page load time can decrease conversions by 7%?

Most businesses lose thousands of dollars because their websites are slow, not mobile-friendly, or don't convert visitors into customers.

Here's what I focus on when building websites:

🚀 PERFORMANCE FIRST
- 95+ Google PageSpeed scores (like valyoued.co.in)
- Optimized images, code, and server response
- Fast loading on mobile and desktop

📱 MOBILE EXPERIENCE  
- Mobile-first design approach
- Touch-friendly interfaces
- Responsive across all devices

💰 CONVERSION OPTIMIZATION
- Strategic placement of call-to-actions
- Lead capture forms that actually work
- User experience that guides visitors to buy

🔧 TECHNICAL EXCELLENCE
- Clean, maintainable code
- SEO-optimized structure
- Security best practices

Want to see what this looks like for your business?

I'd be happy to do a free audit of your current website and show you exactly what's holding back your conversions.

Book a call: [CALENDAR_LINK]

Best,
Harish

P.S. - Even if you don't work with me, you'll walk away with actionable insights to improve your site immediately.`,
            actionItems: ['free_audit_offer', 'performance_showcase']
          },
          {
            delay: 3 * 24 * 60 * 60 * 1000, // 3 days
            type: 'project_showcase_email',
            subject: '🏗️ Behind the Scenes: Building a $50K+ E-commerce Platform',
            content: `[NAME],

Thought you'd find this interesting...

Last quarter, I built a custom e-commerce platform that's now processing $50K+ in monthly sales.

THE CHALLENGE:
- Client needed more than Shopify could offer
- Required complex inventory management
- Needed integration with existing CRM
- Had to handle high traffic during launch

THE SOLUTION:
✅ Custom React frontend for lightning-fast user experience
✅ PHP backend with robust API architecture  
✅ MySQL database optimized for performance
✅ Payment gateway integration with fraud protection
✅ Admin dashboard for easy management

THE RESULT:
💰 $50K+ monthly sales within 3 months
⚡ 98 PageSpeed score on mobile
📈 35% conversion rate improvement vs. old site
🛡️ Zero security issues or downtime

This is the kind of work I do when businesses need more than a template solution.

If you're ready to build something that scales with your business:

Let's talk: [CALENDAR_LINK]

Best regards,
Harish Kumar

P.S. - Whether you need a simple WordPress site or complex web application, I approach every project with the same attention to detail and business focus.`,
            actionItems: ['showcase_complex_project', 'custom_solutions']
          },
          {
            delay: 7 * 24 * 60 * 60 * 1000, // 1 week  
            type: 'final_cta_email',
            subject: '[NAME] - Ready to Start Your Website Project?',
            content: `[NAME],

I wanted to follow up on your website project inquiry.

Over the past week, I've shared:
✅ How I approach web development differently
✅ The performance and conversion strategies I use
✅ Real examples from recent projects

If you're still considering your options, here's what some of my recent clients have said:

"Harish didn't just build us a website - he built us a sales machine. Our leads increased 400% in the first month." - Sarah, E-commerce Store Owner

"Best investment we made. The site loads instantly and our customers love the user experience." - Mike, SaaS Startup

"Finally, a developer who understands business, not just code." - Jennifer, Marketing Agency

READY TO GET STARTED?
📞 Book a project discussion call: [CALENDAR_LINK]
📧 Or simply reply with your project details

NOT READY YET?
That's totally fine. Keep my contact info handy for when you are:
• Email: imdigitalharish@gmail.com
• Phone: +91 96020 03790

I'm here when you're ready to build something great.

Best,
Harish Kumar

P.S. - My calendar fills up fast, especially for Q1 2025. If you're thinking about starting a project in the next few months, it's worth having a conversation now to secure your spot.`,
            actionItems: ['final_cta', 'testimonials', 'calendar_booking']
          }
        ]
      },

      // Full-Stack (Both Services) Sequences
      'full-stack-digital': {
        name: 'Complete Digital Solutions Lead Nurture',
        triggers: ['form_submit', 'pricing_view', 'service_selection'],
        sequence: [
          {
            delay: 0,
            type: 'welcome_email',
            subject: '🚀 Ready to Build Your Complete Digital Empire?',
            content: `[NAME],

Wow! I love working with visionaries who understand that successful businesses need BOTH great technology AND powerful marketing.

You're interested in complete digital solutions - that puts you ahead of 90% of businesses that try to piece together their web and marketing efforts separately.

HERE'S WHAT MAKES MY APPROACH UNIQUE:
🔧 I build AND market your digital presence
📈 Every website is designed with conversion and SEO in mind  
🎯 Every marketing campaign drives traffic to optimized landing pages
📊 You get integrated analytics that show the complete customer journey

WHAT HAPPENS NEXT:
1. I'll personally review your current digital situation
2. Create a custom strategy combining web + marketing
3. Show you exactly how this integrated approach will grow your business

Ready to build your digital empire?

Call me directly: +91 96020 03790
Or book a strategy session: [CALENDAR_LINK]

Excited to work with you!

Harish Kumar
Full-Stack Digital Architect

P.S. - I only take on 1-2 complete digital transformation projects at a time. These are intensive, results-focused partnerships that typically deliver 8X+ ROI.`,
            actionItems: ['complete_audit', 'strategy_session']
          },
          {
            delay: 24 * 60 * 60 * 1000, // 24 hours
            type: 'integrated_approach_email',
            subject: '💡 Why Most Businesses Fail at Digital (And How You Can Win)',
            content: `[NAME],

Let me share something that might surprise you...

95% of businesses make the same costly mistake:

They build their website with one team, then hire a different agency for marketing.

THE RESULT?
❌ Website not optimized for marketing campaigns
❌ Marketing drives traffic to pages that don't convert  
❌ No integrated tracking or optimization
❌ Wasted ad spend and poor user experience

THE BETTER WAY (My Integrated Approach):
✅ Website built specifically to convert marketing traffic
✅ Landing pages designed for each campaign
✅ Integrated analytics tracking every touchpoint  
✅ Continuous optimization of both web and marketing

REAL EXAMPLE:
When I worked with Valyoued, I:
🏗️ Built a custom website optimized for their audience
📈 Created content and campaigns to drive traffic
📊 Set up funnels that convert visitors to leads
🚀 Result: 27K+ monthly reach and consistent lead flow

This integrated approach is why my clients see 8X average ROI instead of the industry average of 2-3X.

Want to see how this would work for your business?

Let's create your custom digital strategy: [CALENDAR_LINK]

Best,
Harish

P.S. - This approach isn't for everyone. It requires a bigger investment upfront, but the results speak for themselves. My complete digital transformation clients typically see ROI within 60 days.`,
            actionItems: ['integrated_case_study', 'roi_calculator']
          },
          {
            delay: 3 * 24 * 60 * 60 * 1000, // 3 days
            type: 'complete_system_email',
            subject: '🎯 The Complete Digital Ecosystem That Grew [CLIENT] 800%',
            content: `[NAME],

I want to show you exactly what a complete digital ecosystem looks like...

CASE STUDY: Local Service Business → Industry Authority

STARTING POINT:
- Outdated website with poor mobile experience
- No online marketing strategy  
- Sporadic social media presence
- Getting 2-3 leads per month

THE COMPLETE SOLUTION I BUILT:

🏗️ FOUNDATION (Custom Website)
- Mobile-first responsive design
- Conversion-optimized landing pages
- Advanced lead capture system
- 95+ Google PageSpeed score

📈 TRAFFIC ENGINE (Marketing System)  
- Strategic content marketing
- Local SEO optimization
- Social media automation
- Google & Facebook ad campaigns

🔄 CONVERSION MACHINE (Automated Funnels)
- Email nurture sequences
- Automated follow-up systems  
- CRM integration and tracking
- Performance monitoring dashboard

THE RESULTS (6 months):
📊 800% increase in qualified leads (2 → 18 per month)
💰 Revenue growth from $15K to $75K monthly
🏆 Became the go-to authority in their local market
⚡ 24/7 automated lead generation system

THIS COULD BE YOUR STORY.

Ready to build your complete digital ecosystem?

Book your strategy session: [CALENDAR_LINK]

Best,
Harish Kumar

P.S. - Complete digital transformations are intensive projects. I only take on 1-2 at a time to ensure exceptional results. If you're ready to make a serious investment in your business growth, let's talk.`,
            actionItems: ['complete_transformation_case_study', 'roi_projection']
          }
        ]
      }
    };
  }

  // Trigger sequence based on user action
  triggerSequence(serviceType, triggerAction, userData = {}) {
    const sequence = this.sequences[serviceType];
    if (!sequence) return;

    if (sequence.triggers.includes(triggerAction)) {
      this.startSequence(serviceType, userData);
      
      // Track sequence initiation
      portfolioAnalytics.logEvent({
        type: 'sequence_started',
        sessionId: portfolioAnalytics.sessionId,
        timestamp: Date.now(),
        data: {
          sequenceType: serviceType,
          trigger: triggerAction,
          userData
        }
      });
    }
  }

  // Start automated sequence
  startSequence(serviceType, userData) {
    const sequenceId = `${Date.now()}_${serviceType}`;
    const sequence = this.sequences[serviceType];
    
    this.activeSequences.set(sequenceId, {
      type: serviceType,
      userData,
      startTime: Date.now(),
      currentStep: 0
    });

    // Schedule all emails in the sequence
    sequence.sequence.forEach((email, index) => {
      setTimeout(() => {
        this.sendSequenceEmail(sequenceId, email, userData);
      }, email.delay);
    });

    return sequenceId;
  }

  // Send email in sequence
  async sendSequenceEmail(sequenceId, emailData, userData) {
    try {
      // Personalize email content
      const personalizedContent = this.personalizeContent(emailData.content, userData);
      const personalizedSubject = this.personalizeContent(emailData.subject, userData);

      // Send email via backend
      const emailPayload = {
        to: userData.email,
        subject: personalizedSubject,
        content: personalizedContent,
        type: emailData.type,
        sequenceId,
        actionItems: emailData.actionItems
      };

      // Track email sent
      portfolioAnalytics.logEvent({
        type: 'sequence_email_sent',
        timestamp: Date.now(),
        data: {
          sequenceId,
          emailType: emailData.type,
          recipient: userData.email,
          subject: personalizedSubject
        }
      });

      // Send to backend email service
      await fetch('/api/send-sequence-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailPayload)
      });

      console.log(`📧 Sequence email sent: ${emailData.type} to ${userData.email}`);
      
    } catch (error) {
      console.error('Failed to send sequence email:', error);
    }
  }

  // Personalize email content
  personalizeContent(content, userData) {
    let personalized = content;
    
    // Replace placeholders
    personalized = personalized.replace(/\[NAME\]/g, userData.name || 'there');
    personalized = personalized.replace(/\[EMAIL\]/g, userData.email || '');
    personalized = personalized.replace(/\[COMPANY\]/g, userData.company || '');
    personalized = personalized.replace(/\[SERVICE\]/g, userData.service || '');
    
    // Add calendar and case study links
    personalized = personalized.replace(/\[CALENDAR_LINK\]/g, 'https://cal.com/harishkumar');
    personalized = personalized.replace(/\[CASE_STUDY_LINK\]/g, 'https://portfolioweb.fliphatmedia.com/case-studies');

    return personalized;
  }

  // Get sequence performance analytics
  getSequenceAnalytics() {
    const analytics = {
      activeSequences: this.activeSequences.size,
      sequencesByType: {},
      totalEmails: 0,
      performance: {}
    };

    // Calculate sequence performance
    this.activeSequences.forEach((sequence, id) => {
      if (!analytics.sequencesByType[sequence.type]) {
        analytics.sequencesByType[sequence.type] = 0;
      }
      analytics.sequencesByType[sequence.type]++;
    });

    return analytics;
  }

  // Stop sequence (for unsubscribes)
  stopSequence(sequenceId) {
    if (this.activeSequences.has(sequenceId)) {
      this.activeSequences.delete(sequenceId);
      
      portfolioAnalytics.logEvent({
        type: 'sequence_stopped',
        timestamp: Date.now(),
        data: { sequenceId }
      });
    }
  }
}

// Global follow-up sequences instance
const followUpSequences = new FollowUpSequences();

export default followUpSequences;
